import React, { useEffect, useRef } from 'react';
import MediaCommentInput from './MediaCommentInput';
import styled from 'styled-components';
import { formatDate } from 'utils/formatting';
import { MediaCommentsInterface } from './MediaModal';
import { useMediaGalleryPageContext } from 'pages/MediaGalleryPage/media-gallery-page.context';
import { CircularProgress } from '@mui/material';

const SidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 25rem;
  min-width: 25rem;
  justify-content: space-between;
  padding: 0 1rem;

  ${(props) => props.theme.breakpoints.down('md')} {
    width: 100%;
    height: 35rem;
    min-width: 18rem;
  }

  background-color: ${(props) => props.theme.colors.neutral700};
`;

const InputAreaWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-top: 1px solid;
  color: ${(props) => props.theme.colors.neutral200};

  border-color: ${(props) => props.theme.colors.neutral200};
`;

const CommentBody = styled.div`
  padding-bottom: 0.3rem;
  overflow-wrap: anywhere;
`;

const CommentList = styled.div`
  margin: 1rem 0;
  padding-right: 2rem;
  overflow-y: auto;
  padding-bottom: 0.5rem;

  ::-webkit-scrollbar {
    background: ${(props) => props.theme.colors.neutral600};
    -webkit-border-radius: 0.625rem;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.dropShadow};
    -webkit-border-radius: 0.625rem;

    &:hover {
      background: #6e6c7a;
    }
  }
`;

const NoCommentText = styled.div`
  font-size: 1.4rem;
  text-align: center;
  padding-top: 2rem;
  color: ${(props) => props.theme.colors.neutral200};
`;

const SingleCommentBackground = styled.div`
  display: inline-block;
  margin-top: 0.5rem;
  min-height: 3.5rem;
  border-radius: 0.625rem;

  background-color: ${(props) => props.theme.colors.neutral600};
`;

const CommentDetailWrapper = styled.div`
  padding: 0.5rem;
  color: ${(props) => props.theme.colors.neutral200};
`;

const UsersName = styled.div`
  padding: 0 0.3rem 0.3rem 0;
  font-size: 85%;
  font-weight: 500;
  white-space: nowrap;
`;

const Timestamp = styled.div`
  font-size: 75%;
  margin: 0.12rem 0 0 0.12rem;
  font-weight: 400;
  white-space: nowrap;
`;

const HeaderWrapper = styled.div`
  display: flex;
`;

const StyledCircularProgress = styled(CircularProgress)`
  margin: 2rem;
`;

const MediaCommentList = ({
  comments,
  id,
}: {
  comments?: MediaCommentsInterface[];
  id?: number;
}) => {
  const { createCommentLoading } = useMediaGalleryPageContext();
  const bottomOfCommentsRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    bottomOfCommentsRef.current?.scrollIntoView();
  };

  useEffect(() => {
    scrollToBottom();
  }, [comments?.length]);

  const abbreviateUserName = (firstName?: string, lastName?: string) => {
    if (!firstName || !lastName) return '';

    if ((firstName + lastName).length > 30) {
      return `${firstName} ${lastName[0]}.`;
    } else {
      return `${firstName} ${lastName}`;
    }
  };

  return (
    <SidebarWrapper>
      <CommentList>
        {!comments?.length && <NoCommentText>No comments yet</NoCommentText>}
        {comments?.map((comment) => (
          <div key={comment.id}>
            <SingleCommentBackground>
              <CommentDetailWrapper>
                <HeaderWrapper>
                  <UsersName>
                    {abbreviateUserName(comment.user?.firstName, comment.user?.lastName)}
                  </UsersName>
                  <Timestamp>{formatDate(comment.createdAt, '', true)}</Timestamp>
                </HeaderWrapper>
                <CommentBody>{comment.body}</CommentBody>
              </CommentDetailWrapper>
            </SingleCommentBackground>
          </div>
        ))}
        {createCommentLoading && <StyledCircularProgress size={'3rem'} />}
        <div ref={bottomOfCommentsRef} />
      </CommentList>
      <InputAreaWrapper>
        <MediaCommentInput id={id} />
      </InputAreaWrapper>
    </SidebarWrapper>
  );
};

export default MediaCommentList;
